import React, { memo, useEffect, useRef } from 'react';
import algoIcon from '../../algo.webp';
import {
  auraRgbToStyleVars,
  pickHomeHeroAuraStep,
  HERO_AURA_DIGIT_COLORS,
} from '../../utils/auraColor';

const AURA_CSS_VAR_KEYS = [
  '--aura-rgb-light',
  '--aura-rgb',
  '--aura-rgb-dark',
  '--aura-rgb-darker',
  '--aura-rgb-darkest',
  '--aura-digit-highlight',
  '--aura-glow-soft',
  '--aura-glow-mid',
  '--aura-glow-strong',
  '--aura-glow-digit',
  '--aura-icon-glow-1',
  '--aura-icon-glow-2',
];

const matrixColumnsCache = new Map();

function applyAuraStyleVars(element, vars) {
  if (!element) return;
  if (!vars) {
    AURA_CSS_VAR_KEYS.forEach((key) => element.style.removeProperty(key));
    return;
  }
  Object.entries(vars).forEach(([key, value]) => {
    element.style.setProperty(key, value);
  });
}

function pickMixedDigitTone(mix) {
  if (mix === 'none') return { tone: 'aura' };
  if (Math.random() >= 0.5) return { tone: 'orange' };
  if (mix === 'rainbow') {
    return {
      tone: 'rainbow',
      rainbowColor: HERO_AURA_DIGIT_COLORS[Math.floor(Math.random() * HERO_AURA_DIGIT_COLORS.length)],
    };
  }
  return { tone: 'aura' };
}

function buildMatrixArrowColumns(mix) {
  const columnCount = 7;
  return Array.from({ length: columnCount }, (_, i) => {
    const charCount = 18 + Math.floor(Math.random() * 10);
    const chars = Array.from({ length: charCount }, () => ({
      digit: Math.floor(Math.random() * 10),
      ...pickMixedDigitTone(mix),
    }));
    return {
      id: i,
      left: ((i + Math.random() * 0.55) / columnCount) * 100,
      duration: (3.4 + Math.random() * 3.4).toFixed(2),
      delay: (-Math.random() * 4).toFixed(2),
      fontSize: (0.7 + Math.random() * 0.4).toFixed(2),
      chars,
    };
  });
}

export function getMatrixArrowColumns(mix = 'none') {
  const cacheKey = `${mix}::slow-v2`;
  if (!matrixColumnsCache.has(cacheKey)) {
    matrixColumnsCache.set(cacheKey, buildMatrixArrowColumns(mix));
  }
  return matrixColumnsCache.get(cacheKey);
}

/** @deprecated Use getMatrixArrowColumns — kept for any external imports. */
export function useMatrixArrowColumns({ mix = 'none' } = {}) {
  return getMatrixArrowColumns(mix);
}

/**
 * Cycle aura colors on the accent layer only (cloud/icon/label), never on the
 * digit rain — inherited @property transitions were restarting column animations.
 *
 * Color is driven by the caller: a fixed `auraRgb`, or a random step applied on
 * each `colorStep` change (the effect re-runs, flipping the aura in sync).
 */
function useHeroMatrixAuraColors(accentRef, rainRef, { auraRgb, colorStep, auraZoneRef }) {
  const prevKeyRef = useRef(null);

  useEffect(() => {
    const accent = accentRef.current;
    const auraTarget = auraZoneRef?.current || accent;
    if (!auraTarget) return;

    if (auraRgb) {
      const vars = auraRgbToStyleVars(auraRgb);
      applyAuraStyleVars(auraTarget, vars);
      applyAuraStyleVars(rainRef.current, vars);
      return;
    }

    applyAuraStyleVars(rainRef.current, null);

    if (colorStep == null) {
      applyAuraStyleVars(auraTarget, null);
      return;
    }

    const step = pickHomeHeroAuraStep(prevKeyRef.current);
    prevKeyRef.current = step.key;
    applyAuraStyleVars(auraTarget, step.vars);
  }, [auraRgb, colorStep, accentRef, rainRef, auraZoneRef]);
}

const HeroMatrixRain = memo(function HeroMatrixRain({ mix }) {
  const matrixArrowColumns = getMatrixArrowColumns(mix);

  return (
    <div className="hero-matrix-aura__rain" aria-hidden="true">
      {matrixArrowColumns.map((col) => (
        <span
          key={col.id}
          className="matrix-arrow__col"
          style={{
            left: `${col.left}%`,
            animationDuration: `${col.duration}s`,
            animationDelay: `${col.delay}s`,
            fontSize: `${col.fontSize}rem`,
          }}
        >
          {col.chars.map((entry, idx) => (
            <i
              key={idx}
              className={`matrix-arrow__digit${
                entry.tone === 'orange'
                  ? ' matrix-arrow__digit--orange'
                  : entry.tone === 'rainbow'
                    ? ' matrix-arrow__digit--rainbow'
                    : ''
              }`}
              style={
                entry.tone === 'rainbow' && entry.rainbowColor
                  ? { '--matrix-digit-color': entry.rainbowColor }
                  : undefined
              }
            >
              {entry.digit}
            </i>
          ))}
        </span>
      ))}
    </div>
  );
});

const DEFAULT_CLOUD_HINT = 'get a vibe match';

export default function HeroMatrixAura({
  className = '',
  style,
  auraRgb = null,
  colorStep = null,
  cloudHint = DEFAULT_CLOUD_HINT,
  /** Extra class on the cloud hint line, e.g. `hero-aura-cloud__letters--status` for status copy. */
  cloudHintClassName = '',
  showIcon = true,
  showLabel = true,
  showCloud = true,
  showLabelHelp = false,
  onLabelHelpClick,
  labelHelpAriaLabel = 'More about the visual vibe transformer',
  showCloudHelp = false,
  onCloudHelpClick,
  cloudHelpAriaLabel = 'More about your vibe',
  auraZoneRef = null,
}) {
  const accentRef = useRef(null);
  const rainRef = useRef(null);
  const digitMix = auraRgb ? 'aura' : 'rainbow';

  useHeroMatrixAuraColors(accentRef, rainRef, {
    auraRgb,
    colorStep,
    auraZoneRef,
  });

  const openLabelHelp = (e) => {
    e.stopPropagation();
    if (typeof onLabelHelpClick === 'function') onLabelHelpClick(e);
  };

  const showAccentContent = showIcon || showLabel || showCloud;

  return (
    <div
      className={`hero-matrix-aura${
        !showAccentContent ? ' hero-matrix-aura--rain-only' : ''
      }${className ? ` ${className}` : ''}`}
      aria-hidden={showCloudHelp || showLabelHelp ? undefined : true}
      style={style}
    >
      <div ref={rainRef} className="hero-matrix-aura__rain-host">
        <HeroMatrixRain mix={digitMix} />
      </div>
      {showAccentContent ? (
      <div ref={accentRef} className="hero-matrix-aura__accent">
        {showIcon || showLabel || showLabelHelp ? (
          <div className="matrix-arrow">
            <div className="matrix-arrow__center">
              {showIcon ? (
                <span className="matrix-arrow__icon-wrap">
                  <img
                    src={algoIcon}
                    alt=""
                    className="matrix-arrow__icon"
                    aria-hidden
                  />
                </span>
              ) : null}
              {showLabel ? (
                <div
                  className={`matrix-arrow__label-wrap${
                    showLabelHelp ? ' matrix-arrow__label-wrap--interactive' : ''
                  }`}
                >
                  {showLabelHelp ? (
                    <button
                      type="button"
                      className="matrix-arrow__label-btn"
                      aria-label={labelHelpAriaLabel}
                      onClick={openLabelHelp}
                    >
                      <span className="matrix-arrow__label">
                        <span className="matrix-arrow__label-subline">
                          <span className="matrix-arrow__label-accent">v</span>isual-
                          <span className="matrix-arrow__label-accent">v</span>ibe{' '}
                          <span className="matrix-arrow__label-accent">t</span>rans
                          <span className="matrix-arrow__label-accent">f</span>ormer
                        </span>
                      </span>
                    </button>
                  ) : (
                    <span className="matrix-arrow__label">
                      <span className="matrix-arrow__label-subline">
                        <span className="matrix-arrow__label-accent">v</span>isual-
                        <span className="matrix-arrow__label-accent">v</span>ibe{' '}
                        <span className="matrix-arrow__label-accent">t</span>rans
                        <span className="matrix-arrow__label-accent">f</span>ormer
                      </span>
                    </span>
                  )}
                  {showLabelHelp ? (
                    <button
                      type="button"
                      className="saved-poster-match-help matrix-arrow__label-help"
                      aria-label={labelHelpAriaLabel}
                      onClick={openLabelHelp}
                    >
                      ?
                    </button>
                  ) : null}
                </div>
              ) : showLabelHelp ? (
                <button
                  type="button"
                  className="saved-poster-match-help matrix-arrow__label-help"
                  aria-label={labelHelpAriaLabel}
                  onClick={openLabelHelp}
                >
                  ?
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
        {showCloud ? (
        <div className="hero-matrix-aura__cloud-wrap">
          <div className="hero-aura-cloud">
            <span className="hero-aura-cloud__blob hero-aura-cloud__blob--one" />
            <span className="hero-aura-cloud__blob hero-aura-cloud__blob--two" />
            <span className="hero-aura-cloud__blob hero-aura-cloud__blob--three" />
            <div className="hero-aura-cloud__content">
              <p
                className={`hero-aura-cloud__letters hero-sample-hint${
                  cloudHintClassName ? ` ${cloudHintClassName}` : ''
                }`}
              >
                {cloudHint}
              </p>
              {showCloudHelp ? (
                <button
                  type="button"
                  className="saved-poster-match-help hero-aura-cloud__help-btn"
                  aria-label={cloudHelpAriaLabel}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (typeof onCloudHelpClick === 'function') onCloudHelpClick(e);
                  }}
                >
                  ?
                </button>
              ) : null}
            </div>
          </div>
        </div>
        ) : null}
      </div>
      ) : null}
    </div>
  );
}
